import styled from '@emotion/styled';
import { SM_SCREEN_MAX } from '../../modules/Styleguide';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  padding: 100px 0;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding: 50px 0;
  }
`;

const FeaturesSection = styled(Section)`
  flex-direction: column;
  padding: 50px 0;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding: 20px;
  }
`;

const FeatureSectionHeader = styled.div`
  margin: 0 90px 50px;
  max-width: 930px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    margin: 50px 0;
  }
`;

const FeatureWell = styled.div`
  width: 30px;
  position: relative;
  display: flex;
  align-items: stretch;
  margin-right: 55px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    margin-right: 20px;
  }
`;

const WellHead = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 100%;
  background: #e4d9fa;
  position: absolute;
  top: 0;
  margin-top: 7px;
`;

const WellTail = styled.div`
  width: 6px;
  background: #e4d9fa;
  position: absolute;
  top: 25px;
  left: 7px;
  height: calc(100% + 40px);

  @media (max-width: ${SM_SCREEN_MAX}px) {
    height: calc(100% + 80px);
  }
`;

const Feature = styled.div`
  display: flex;
  padding-bottom: 40px;

  &:last-child {
    .well-tail {
      display: none;
    }
  }

  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding-bottom: 80px;
  }
`;

const FeaturesContainer = styled.div`
  max-width: 750px;
`;

const FeatureMain = styled.div`
  width: 100%;
`;

const FeatureHeader = styled.div`
  margin-bottom: 30px;
`;

const FeatureContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const FeatureAsset = styled.div`
  @media (max-width: ${SM_SCREEN_MAX}px) {
    margin-bottom: 20px;
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const FeatureInfo = styled.p`
  max-width: 350px;
  margin-left: 35px;
  font-size: 14px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    margin-left: 0;
    max-width: 100%;
  }
`;

const SearchSection = styled(Section)`
  background-color: #8940fa;
`;

const SearchSectionBackground = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const SearchContent = styled.div`
  width: 100%;
  max-width: 1015px;
  padding: 0 30px;
`;

const HowToSwitchSection = styled(Section)`
  background: #292f36;
  color: #ffffff;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding-bottom: 0;
  }
`;

const HowToSwitchContent = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const HowToSwitchSubheader = styled.div`
  margin: auto 0 65px;
  display: flex;
  justify-content: center;

  h2 {
    font-weight: normal;
    font-size: 18px;
    margin-top: 10px;
  }

  @media (max-width: ${SM_SCREEN_MAX}px) {
    margin: 10px 45px;
    text-align: center;
    line-height: 19px;

    h2 {
      font-size: 14px;
    }
  }
`;

const Video = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 50px;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding: 50px 30px 0;
    div:first-child {
      margin-top: 0;
    }
  }
`;

const StepContainer = styled.div`
  width: 200px;
  margin: 15px 10px;

  p {
    color: #ffffff;
  }

  @media (max-width: ${SM_SCREEN_MAX}px) {
    width: 100%;
    margin: 30px 0;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const StepTitle = styled.div`
  margin-bottom: 8px;

  h2 {
    font-weight: bold;
    font-size: 16px;
  }
`;

const QuotesSection = styled(Section)`
  background-color: #ffffff;
  padding: 100px 30px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding: 50px 10px;
  }
`;

const QuotesContent = styled.div`
  max-width: 966px;
`;

const QuotesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuoteContainer = styled.div<{ isReverse: boolean }>`
  width: 100%;
  background-color: #ffffff;
  margin: 30px 0;
  display: flex;
  ${({ isReverse }) =>
    isReverse &&
    `
      flex-direction: row-reverse;
    `}
    
    align-items: ${({ isReverse }) => (isReverse ? 'flex-start' : 'center')};
    justify-content: space-between;
    
    @media (max-width: ${SM_SCREEN_MAX}px) {
      flex-direction: column;
      align-items: center;
    }
`;

const QuoteImage = styled.div<{ isReverse: boolean }>`
  ${({ isReverse }) =>
    isReverse &&
    `
      position: relative;
      top: -30px;
    `}

  img {
    border-radius: 10px;
  }

  @media (max-width: ${SM_SCREEN_MAX}px) {
    flex-direction: column;
    align-items: center;

    img {
      width: 100%;
    }
  }
`;

const QuoteTitle = styled.div`
  width: 100%;
  padding-left: 18px;
  border-left: solid 1px #000000;

  p:first-child {
    font-size: 16px;
    margin-bottom: 0;
  }
`;

const Quote = styled.div<{ isReverse: boolean }>`
  width: 100%;
  display: flex;
  ${({ isReverse }) =>
    isReverse ? 'margin-right: 55px;' : 'margin-left: 35px;'}

    @media (max-width: ${SM_SCREEN_MAX}px) {
      margin: 20px 0 0;
      flex-direction: column;
    }
`;

const QuoteIconContainer = styled.div<{ isReverse: boolean }>`
  ${({ isReverse }) =>
    !isReverse &&
    `
      
    `}

  @media (min-width: ${SM_SCREEN_MAX}px) {
    position: relative;
    width: 75px;
    margin-right: 11px;
  }
`;

const QuoteIcon = styled.div<{ isReverse: boolean }>`
  position: absolute;
  top: -30px;

  ${({ isReverse }) =>
    !isReverse &&
    `
      &:not(:first) {
        top: 25px;
      }

      $:last {
        top: 94px;
      }
    `}
    
  @media (max-width: ${SM_SCREEN_MAX}px) {
    position: relative;
    top: 0;
  }
`;

const QuoteText = styled.div<{ isReverse: boolean }>`
  max-width: ${({ isReverse }) => (isReverse ? 325 : 500)}px;
  p {
    font-size: 18px;
    line-height: 26px;
    margin-bottom: 40px;
  }

  @media (max-width: ${SM_SCREEN_MAX}px) {
    max-width: 100%;
  }
`;

const FaqSection = styled(Section)`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 70px 30px;
  background: #ffffff;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    padding: 50px 10px;
  }
`;

const FaqContent = styled.div`
  max-width: 780px;
  width: 100%;
`;

const FAQol = styled.ol`
  li {
    margin-bottom: 10px;
  }

  &:last-child {
    margin: 0;
  }
`;

const SectionHeader = styled.h1`
  color: #000000;
  font-weight: bold;
  font-size: 36px;
  text-align: center;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    font-size: 21px;
  }
`;

const SearchHeading = styled(SectionHeader)`
  color: #ffffff;
  font-weight: 48px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    font-size: 28px;
  }
`;

const FeatureHeading = styled.h2`
  font-size: 28px;
  color: #000000;
  font-weight: bold;
  margin-top: 0px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    font-size: 18px;
  }
`;

const FeatureSubHeading = styled.h3`
  font-size: 16px;
  font-weight: normal;
  line-height: 23px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    font-size: 14px;
  }
`;

const SearchSubHeading = styled.h3`
  font-size: 18px;
  font-weight: normal;
  color: #ffffff;
  text-align: center;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    font-size: 14px;
  }
`;

const FAQHeader = styled.h2`
  color: #979797;
  font-weight: bold;
  font-size: 21px;

  @media (max-width: ${SM_SCREEN_MAX}px) {
    font-size: 16px;
  }
`;

export {
  Container,
  SearchSection,
  SearchSectionBackground,
  SearchContent,
  HowToSwitchSection,
  HowToSwitchContent,
  Video,
  StepsContainer,
  StepContainer,
  IconContainer,
  StepTitle,
  QuotesSection,
  QuotesContent,
  QuotesContainer,
  QuoteContainer,
  QuoteTitle,
  Quote,
  QuoteIconContainer,
  QuoteIcon,
  FaqSection,
  FaqContent,
  FeaturesSection,
  FeatureSectionHeader,
  Feature,
  FeatureHeader,
  FeatureContent,
  FeatureAsset,
  FeatureInfo,
  FeatureWell,
  FeatureMain,
  FeaturesContainer,
  WellHead,
  WellTail,
  HowToSwitchSubheader,
  FAQol,
  FAQHeader,
  SectionHeader,
  QuoteText,
  QuoteImage,
  SearchHeading,
  SearchSubHeading,
  FeatureHeading,
  FeatureSubHeading,
};
